import { useMemo } from 'react'
import * as React from 'react'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import MultiRoomHotelMultiCartReviewGroup from './MultiRoomHotelMultiCartReviewGroup'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import useUserMultiCartHotelItemData from '../hooks/useUserMultiCartHotelItemData'
import { queryKeyAdults, queryKeyCheckIn, queryKeyCheckOut, queryKeyChildren } from 'constants/url'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import { isAccommodationCheckInDatePast } from 'lib/datetime/dateUtils'
import { getAccommodationPropertyTimeZone } from 'lib/datetime/accommodationPropertyTimeZone'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import UndoDeletedItemsTile from '../UndoDeletedItemsTile'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'
import type { CartItemData } from '../hooks/useAllUserMultiCartItemsMetadata'
import { getMultiCartGroupPublicTotal } from './multiCartReviewGroupPricing'

interface Props {
  multiCartItems: Array<App.MultiCartInstantBookingLEHotelItem>
  cartItemsData: Array<CartItemData>
  tileVariant?: 'default' | 'condensed'
}

function LeHotelMultiCartReviewGroup({ multiCartItems, cartItemsData, tileVariant = 'default' }: Props) {
  // ============================================================================
  // HOOKS AND CONTEXT
  // ============================================================================
  const referenceMultiCartItem = multiCartItems[0]
  const refCartItem = useUserMultiCartHotelItemData(referenceMultiCartItem.item as App.Checkout.InstantBookingLEHotelItem)
  const propertyTimeZone = getAccommodationPropertyTimeZone(refCartItem.offer)
  const multiCartActorRef = MultiCartContext.useActorRef()
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()
  // State selectors
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const allMultiCartItems = useSelector(multiCartActorRef, (snapshot) => {
    if (snapshot.context.cartItems.status !== 'loaded' || !snapshot.context.activeCartId) return []
    return Object.values(snapshot.context.cartItems.data[snapshot.context.activeCartId] || {})
  })

  // ============================================================================
  // STATE CALCULATIONS
  // ============================================================================
  const areAllItemsSavedForLater = multiCartItems.length > 0 && multiCartItems.every(item => item.state === 'save_for_later')
  const areAllItemsDeleted = multiCartItems.length > 0 && multiCartItems.every(item => item.state === 'deleted')
  const isAnyItemDeleted = multiCartItems.length > 0 && multiCartItems.some(item => item.state === 'deleted')
  const isAnyItemOnPastDates = propertyTimeZone ? multiCartItems.some(item =>
    isAccommodationCheckInDatePast(item.checkIn, { timeZone: propertyTimeZone }),
  ) : false

  const isAnyItemUnavailable = useMemo(() => {
    if (!refCartItem.offer || refCartItem.loading || !refCartItem.loaded) return false

    const endDate = refCartItem.offer.availabilitySchedule?.end
    if (!endDate) return false

    return multiCartItems.some(item => {
      const isDeleted = item.state === 'deleted'
      return !isDeleted && new Date() > new Date(endDate)
    })
  }, [refCartItem.offer, refCartItem.loading, refCartItem.loaded, multiCartItems])

  const isAnyItemSoldOut = useMemo(() => {
    if (!refCartItem.offer || refCartItem.loading || !refCartItem.loaded) return false

    return multiCartItems.some(item => {
      const isDeleted = item.state === 'deleted'
      return !isDeleted && (refCartItem.offer?.isSoldOut || !refCartItem.itemTotals?.isAvailable)
    })
  }, [refCartItem.offer, refCartItem.loading, refCartItem.loaded, multiCartItems, refCartItem.itemTotals])

  // ============================================================================
  // COMPUTED VALUES AND MEMOIZED DATA
  // ============================================================================
  const { hotelName, publicTotalPrice } = useMemo(() => {
    return {
      hotelName: refCartItem.offer?.property.name || refCartItem.offer?.name || '',
      publicTotalPrice: getMultiCartGroupPublicTotal(multiCartItems, cartItemsData, allMultiCartItems),
    }
  }, [cartItemsData, refCartItem, multiCartItems, allMultiCartItems])

  const offerLink = useMemo(() => {
    const params = new URLSearchParams()
    const rooms = multiCartItems.map(i => i.occupancy)

    if (!refCartItem.isPast) {
      params.set(queryKeyCheckIn, referenceMultiCartItem.checkIn)
      params.set(queryKeyCheckOut, referenceMultiCartItem.checkOut)
    }

    params.set(queryKeyAdults, rooms.map(room => room.adults).join(','))
    params.set(queryKeyChildren, rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(','))

    return offerPageURL(refCartItem.offer, params)
  }, [refCartItem.offer, referenceMultiCartItem, refCartItem.isPast, multiCartItems])

  // ============================================================================
  // UI STATE CALCULATIONS
  // ============================================================================
  const bannerTypeVariant = getBannerTypeVariant(isAnyItemOnPastDates, isAnyItemSoldOut, isAnyItemUnavailable, false)
  const groupDisabled = isAnyItemOnPastDates || isAnyItemUnavailable || isAnyItemSoldOut
  const fetching = refCartItem.loading && !refCartItem.loaded

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  const handleSaveAllForLater = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (!activeCartId) return

    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId,
      itemIds: multiCartItems.map(item => item.id),
      saveForLater: !areAllItemsSavedForLater,
    })
  }

  const handleDeleteAll = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (!areAllItemsDeleted && activeCartId) {
      multiCartActorRef.send({
        type: 'delete items from given cart',
        cartId: activeCartId,
        itemIds: multiCartItems.map(item => item.id),
        currentStates: multiCartItems.reduce((acc, item) => {
          acc[item.id] = item.state
          return acc
        }, {} as Record<string, App.MultiCartItemState>),
      })
    }
  }

  // ============================================================================
  // EARLY RETURNS
  // ============================================================================

  if (areAllItemsDeleted) {
    return <UndoDeletedItemsTile
      title={hotelName}
      onUndoClick={() => {
        multiCartActorRef.send({
          itemIds: multiCartItems.map(item => item.id),
          type: 'undo delete',
        })
      }}
    />
  }

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <MultiRoomHotelMultiCartReviewGroup
      multiCartItems={multiCartItems}
      banner={fetching ? null : <CartReviewBanner variant={bannerTypeVariant} to={offerLink} />}
      showOverlay={areAllItemsSavedForLater || groupDisabled}
      hotelName={hotelName}
      checkIn={referenceMultiCartItem.checkIn}
      checkOut={referenceMultiCartItem.checkOut}
      duration={referenceMultiCartItem.duration}
      headerPrice={!!publicTotalPrice && <PriceRowPrice size="S" price={publicTotalPrice} saleUnit="total" signDisplay="never" priceFormat="preciseDollar" />}
      onSaveAllForLater={handleSaveAllForLater}
      onDeleteAll={handleDeleteAll}
      areAllItemsSavedForLater={areAllItemsSavedForLater}
      areAllItemsDeleted={areAllItemsDeleted}
      isAnyItemDeleted={isAnyItemDeleted}
      fetching={fetching}
      groupDisabled={groupDisabled}
      tileVariant={tileVariant}
    />
  )
}

export default LeHotelMultiCartReviewGroup
